import { useState, useRef } from 'react';

/**
 * useDraggableDialog — generic drag-to-move state for fixed-position dialogs.
 * Knows nothing about dialog content — returns position + handlers only.
 * @param {{ top: number, left: number }} initialPos
 * @returns {{
 *   pos: { top: number, left: number },
 *   dragging: boolean,
 *   titlebarProps: { onMouseDown: function, style: object },
 *   dialogProps: { onMouseMove: function, onMouseUp: function },
 *   resetPos: function({ top, left }): void,
 * }}
 */
export function useDraggableDialog(initialPos) {
    const [pos, setPos] = useState(initialPos);
    const [dragging, setDragging] = useState(false);
    const dragOffset = useRef({ x: 0, y: 0 });

    const handleDragStart = (e) => {
        dragOffset.current = { x: e.clientX - pos.left, y: e.clientY - pos.top };
        setDragging(true);
    };

    const handleDragMove = (e) => {
        if (!dragging) return;
        setPos({ left: e.clientX - dragOffset.current.x, top: e.clientY - dragOffset.current.y });
    };

    const handleDragEnd = () => setDragging(false);

    const resetPos = (newPos) => setPos(newPos);

    return {
        pos,
        dragging,
        titlebarProps: {
            onMouseDown: handleDragStart,
            style: { cursor: dragging ? 'grabbing' : 'grab' },
        },
        dialogProps: {
            onMouseMove: handleDragMove,
            onMouseUp: handleDragEnd,
        },
        resetPos,
    };
}
